import { useState, useEffect, useRef } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { supabase } from "./supabase";
import {
  enqueueAnalysis,
  pollAnalysisJob,
  savePendingJob,
  savePendingAnswers,
  loadPendingJob,
  clearPendingJob,
} from "./asyncAnalyze";
import { euLinkStatusFor, isEUStorefront, isGateAnswerCorrect, makeGateQuestion } from "./euPurchase";
import { androidLinkChannel, resolveAndroidLinkStatus } from "./androidExternalLinks";

const API_URL = process.env.REACT_APP_API_URL || "https://backend-long-morning-8596.fly.dev";

// Android app only: Google Play's external content links program (native plugin
// ExternalContentLinkPlugin). Purchases there may only open our Stripe pages through Play's
// launchExternalLink, and only when Play reports the user eligible.
const ExternalContentLink = registerPlugin("ExternalContentLink");
// Android app in the EEA: Google Play's external offers program (native plugin ExternalOfferPlugin),
// the same flow as above, asked only when the US program says no (androidExternalLinks.js).
const ExternalOffer = registerPlugin("ExternalOffer");
const IS_ANDROID_APP = Capacitor.getPlatform() === "android";
// iOS app only: App Review Guideline 3.1.1(a) allows links to purchase outside the app only on the
// United States App Store storefront (native plugin AppStoreRegionPlugin). US storefront: the same
// direct Stripe links as the website; any other storefront: no purchase path. Browsers unchanged.
const AppStoreRegion = registerPlugin("AppStoreRegion");
const IS_IOS_APP = Capacitor.getPlatform() === "ios";
// iOS app on an EU storefront: StoreKit's external purchase custom links (native plugin
// EUExternalPurchasePlugin), allowed by age per country (euPurchase.js). Before each purchase:
// fresh tokens for Apple, then Apple's notice; the Stripe page opens only if the user continues.
const EUExternalPurchase = registerPlugin("EUExternalPurchase");
// Inside the native app (iOS or Android), detected two ways: Capacitor's own check, or the marker
// the app appends to its user agent (capacitor.config.ts appendUserAgent). If the bridge ever
// failed to load, Capacitor would report "web" and the app would get a browser's direct purchase
// links; the marker keeps it on the safe side. Builds before the marker have only the first check.
const IN_APP_SHELL = Capacitor.isNativePlatform() || /\bFlowLineApp\b/.test(navigator.userAgent || "");
// How long the plans screen waits for the native storefront/eligibility checks before treating
// the answer as no (it never shows purchase options while waiting).
const NATIVE_CHECK_TIMEOUT_MS = 10000;

// Rejects if the promise hasn't settled within ms, so a native call that never answers ends in
// the same "no purchase path" as one that fails.
function withTimeout(promise, ms) {
  return Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms))]);
}

// Android app: the token Play issued for a link-out to a Stripe checkout, kept (also across an app
// restart) until the backend has it, so the purchase can be reported to Google Play
// (backend routers/google_external_link.py). { token, checkoutSessionId } or null.
const PENDING_LINK_TOKEN_KEY = "flowline.pendingExternalLinkToken";

function storePendingLinkToken(ref, pending) {
  ref.current = pending;
  try {
    if (pending) localStorage.setItem(PENDING_LINK_TOKEN_KEY, JSON.stringify(pending));
    else localStorage.removeItem(PENDING_LINK_TOKEN_KEY);
  } catch (e) {}
}

// Posts the pending token (a few tries). Done when the backend accepts it, or refuses it for good
// (bad or foreign session); kept for the next try on a network error, 401 or 5xx.
async function sendPendingLinkToken(ref) {
  for (const delayMs of [0, 2000, 5000]) {
    const pending = ref.current;
    if (!pending) return;
    if (delayMs) await new Promise((resolve) => setTimeout(resolve, delayMs));
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/google/external-link-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${currentSession?.access_token}` },
        body: JSON.stringify({ token: pending.token, checkout_session_id: pending.checkoutSessionId }),
      });
      if (res.ok || (res.status >= 400 && res.status < 500 && res.status !== 401)) {
        if (!res.ok) console.error("external link token refused:", res.status);
        if (ref.current === pending) storePendingLinkToken(ref, null);
        return;
      }
    } catch (e) {
      console.error("external link token error:", e);
    }
  }
}

// The EU purchase status for an iOS app user on a non-US storefront: "ineligible" unless the
// storefront is in the EU table, Apple reports the device eligible (iOS 26.2+, payments allowed),
// and the user's birth year puts them at or above that country's threshold. No birth year on file
// (it's optional at signup): "eu_needs_birth_year", and the plans screen asks for it right there.
// We never guess an age.
async function euLinkStatus(countryCode, userId) {
  if (!isEUStorefront(countryCode) || !userId || !Capacitor.isPluginAvailable("EUExternalPurchase")) return "ineligible";
  const { eligible } = await EUExternalPurchase.isEligible();
  if (!eligible) return "ineligible";
  const { data, error } = await supabase.from("profiles").select("birth_year").eq("id", userId).single();
  if (error) return "ineligible";
  return euLinkStatusFor(countryCode, data?.birth_year ?? null, new Date().getFullYear());
}

// Fresh ACQUISITION and SERVICES tokens from Apple, submitted to the backend by the native plugin
// with the current login token. The plugin itself does nothing unless the device is eligible.
// Resolves the plugin's result; rejects only when the plugin call itself fails.
async function requestEUTokens() {
  const { data: { session: currentSession } } = await supabase.auth.getSession();
  return EUExternalPurchase.requestTokens({ accessToken: currentSession?.access_token, apiUrl: API_URL });
}

// Birth-year choices for the iOS EU plans-screen prompt (signup asks for no age data), newest first.
// Only the year is kept (for the iOS EU purchase flow's age rules); the backend accepts 1900 through
// the current year and refuses a year that makes the user under 13 (its message is shown).
const BIRTH_YEARS = Array.from({ length: 101 }, (_, i) => new Date().getFullYear() - i);
// Signup form fields kept in the auth user's metadata until /sync-signup-profile copies them
// to the profile (metadata key == profiles column). age_range and birth_year are no longer asked;
// they stay here for accounts that signed up with them and haven't been synced yet.
const SIGNUP_PROFILE_FIELDS = ["full_name", "skiing_level", "age_range", "birth_year"];

const QUESTIONS = [
  {
    id: "feel_score",
    question: "How did your run feel?",
    type: "slider",
    min: 1,
    max: 10,
  },
  {
    id: "feel_today",
    question: "How do you feel today?",
    type: "choice",
    options: ["Fresh", "Good", "Tired", "Exhausted"],
  },
  {
    id: "skis_used",
    question: "Which skis did you use?",
    type: "text",
    placeholder: "e.g. Rossignol Hero GS",
  },
  {
    id: "snow_conditions",
    question: "What were the snow conditions?",
    type: "choice",
    options: ["Powder", "Groomed", "Icy", "Wet", "Mixed"],
  },
  {
    id: "notes",
    question: "Any notes about your run?",
    type: "text",
    placeholder: "Optional — anything you want to remember",
  },
];

const NAV_ITEMS = [
  { id: "analyze", label: "Analyze", icon: "🎿" },
  { id: "history", label: "History", icon: "📋" },
  { id: "drills", label: "Drills", icon: "🎬" },
  { id: "howItWorks", label: "How It Works", icon: "⚙️" },
  { id: "account", label: "Account", icon: "👤" },
];

// Static pages outside the app (public/<path>/index.html), linked from the footer and the menu.
const INFO_LINKS = [
  { href: "/contact", label: "Contact us", icon: "✉️" },
  { href: "/impressum", label: "Impressum", icon: "ℹ️" },
  { href: "/privacy-policy", label: "Privacy Policy", icon: "🔒" },
  { href: "/terms", label: "Terms of Use", icon: "📄" },
];

// The lines at the bottom of every Ski Coach screen, logged in or not: the name, then the links
// on a line of their own (on one line together they wrapped unevenly on phones). Each link opens
// its static page as its own page, outside the app.
function Footer() {
  return (
    <p style={styles.footer}>
      FlowLine · Your AI Ski Coach
      <br />
      {INFO_LINKS.map((link, i) => (
        <span key={link.href}>{i > 0 && " · "}<a href={link.href} style={styles.footerLink}>{link.label}</a></span>
      ))}
    </p>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecovery, setIsRecovery] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return window.location.hash.includes("type=recovery") || params.get("type") === "recovery";
  });
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [skiingLevel, setSkiingLevel] = useState("");
  const [authError, setAuthError] = useState("");
  // Account deletion (Account tab). accountNotice is shown on the login screen afterwards.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [accountNotice, setAccountNotice] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [file, setFile] = useState(null);
  // One object URL per picked file, revoked when the file changes. Creating it during render
  // leaked a URL on every render and reloaded the "Your Run" video, restarting playback.
  const [fileUrl, setFileUrl] = useState(null);
  useEffect(() => {
    if (!file) { setFileUrl(null); return; }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const [status, setStatus] = useState("idle");
  const [limitMessage, setLimitMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [feedback, setFeedback] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [turns, setTurns] = useState(0);
  const [overallScore, setOverallScore] = useState(null);
  const [phaseScores, setPhaseScores] = useState(null);
  const [angleScores, setAngleScores] = useState(null);
  const [recommendedDrill, setRecommendedDrill] = useState(null);
  const [recommendedDrillVideo, setRecommendedDrillVideo] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [activeTab, setActiveTab] = useState("analyze");
  const [sessions, setSessions] = useState([]);
  const [expandedSession, setExpandedSession] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');
  const [paymentSuccessMessage, setPaymentSuccessMessage] = useState(false);
  // "web": direct Stripe links (browsers, and the iOS app on the US storefront). Otherwise
  // "checking" | "eligible" / "eligible_eu" (Android, via Play's US external content links / EEA
  // external offers) | "eu" / "eu_gated" (iOS app on an EU storefront,
  // after Apple's notice; "eu_gated" behind the parental gate too) | "eu_needs_birth_year" (iOS, EU
  // storefront, no birth year yet: the plans screen asks for it) | "ineligible" (no purchase path).
  // Fails safe: purchase options show only once a check has said yes; "web" is the starting point
  // only in a browser.
  const [externalLinkStatus, setExternalLinkStatus] = useState(IN_APP_SHELL ? "checking" : "web");
  // iOS app on an EU storefront without a birth year on file ("eu_needs_birth_year"): the plans
  // screen's inline birth-year prompt. euRecheck re-runs the storefront/age check once it's saved.
  const [euBirthYear, setEuBirthYear] = useState("");
  const [euBirthYearSaving, setEuBirthYearSaving] = useState(false);
  const [euBirthYearError, setEuBirthYearError] = useState("");
  const [euRecheck, setEuRecheck] = useState(0);
  // The parental gate while it's open: { question, typed, wrong, resolve }.
  const [parentalGate, setParentalGate] = useState(null);
  // Bumped to re-sync the plan, e.g. when the user comes back from an external checkout.
  const [subscriptionRefresh, setSubscriptionRefresh] = useState(0);
  const awaitingExternalReturn = useRef(false);
  // Android app: the latest link-out's Play token until the backend has it (sendPendingLinkToken).
  const pendingLinkToken = useRef(null);
  const analysisResult = useRef(null);
  const resumeChecked = useRef(false);
  const submittedAnswers = useRef(null);
  const subscriptionSyncedFor = useRef(null);
  const signupProfileSyncedFor = useRef(null);
  const euTokensRequestedFor = useRef(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") setIsRecovery(true);
      setSession(session);
      setLoading(false);
    });

    // Fallback: resolve loading if no auth event fires (e.g. truly unauthenticated)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Sync the plan once per signed-in user per page load. `session` changes on every auth
  // event (INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED) and the getSession() fallback, but a
  // token refresh for the same user doesn't need another Stripe sync.
  useEffect(() => {
    const userId = session?.user?.id ?? null;
    if (!userId) {
      subscriptionSyncedFor.current = null; // signed out: the next sign-in syncs again
      return;
    }
    if (subscriptionSyncedFor.current === userId) return;
    subscriptionSyncedFor.current = userId;
    supabase
      .from("profiles")
      .select("subscription_status")
      .eq("id", userId)
      .single()
      .then(async ({ data }) => {
        if (data?.subscription_status) setSubscriptionStatus(data.subscription_status);
        if (
          session.user.email === "janmoritz2000@gmail.com" ||
          session.user.email === "h.voregger@head.com" ||
          session.user.email === "p.egger@at.head.com" ||
          // App Store / Google Play review accounts
          session.user.email === "janmoritz2000+googletestpremium@gmail.com" ||
          session.user.email === "janmoritz2000+appletestpremium@gmail.com"
        ) {
          setSubscriptionStatus("premium");
          return;
        }
        try {
          // Current token, not the one from the event that triggered this: it's the only call.
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          const res = await fetch(`${API_URL}/check-subscription`, {
            method: "POST",
            headers: { Authorization: `Bearer ${currentSession?.access_token}` },
          });
          const result = await res.json();
          if (result.subscription_status) setSubscriptionStatus(result.subscription_status);
        } catch (e) {
          console.error("check-subscription error:", e);
        }
      });
  }, [session, subscriptionRefresh]);

  // Signup stores its form fields only in the auth user's metadata: signUp returns no session
  // (email confirmation), so the browser can't write profiles then. Once per signed-in user per
  // page load, if the profile is still missing any of them, ask the backend to copy them over
  // (it reads the metadata itself). Users without signup metadata are left alone.
  useEffect(() => {
    const user = session?.user;
    const metadata = user?.user_metadata || {};
    const fromSignup = SIGNUP_PROFILE_FIELDS.filter((field) => metadata[field] != null && metadata[field] !== "");
    if (!user || !fromSignup.length || signupProfileSyncedFor.current === user.id) return;
    signupProfileSyncedFor.current = user.id;
    supabase
      .from("profiles")
      .select(SIGNUP_PROFILE_FIELDS.join(", "))
      .eq("id", user.id)
      .single()
      .then(async ({ data, error }) => {
        if (error || !data || fromSignup.every((field) => data[field] != null)) return;
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          await fetch(`${API_URL}/sync-signup-profile`, {
            method: "POST",
            headers: { Authorization: `Bearer ${currentSession?.access_token}` },
          });
        } catch (e) {
          console.error("sync-signup-profile error:", e);
        }
      });
  }, [session]);

  // Android app: the external checkout ends on flowline.ski in the browser, not in the app, so
  // re-sync the plan when the user switches back after launching one.
  useEffect(() => {
    if (!IS_ANDROID_APP) return;
    // A token that couldn't be sent before the app was closed or went to the background.
    try {
      const saved = JSON.parse(localStorage.getItem(PENDING_LINK_TOKEN_KEY) || "null");
      if (saved?.token && saved?.checkoutSessionId) {
        pendingLinkToken.current = saved;
        sendPendingLinkToken(pendingLinkToken);
      }
    } catch (e) {}
    const onVisible = () => {
      if (document.visibilityState === "visible") sendPendingLinkToken(pendingLinkToken);
      if (document.visibilityState !== "visible" || !awaitingExternalReturn.current) return;
      awaitingExternalReturn.current = false;
      subscriptionSyncedFor.current = null;
      setSubscriptionRefresh((n) => n + 1);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Android app: ask Play whether external purchase links are allowed each time the plans
  // screen opens: the US program first, then the EEA one ("eligible" / "eligible_eu" /
  // "ineligible"). Anything but a clear yes (old app build without the plugin, errors) is a no.
  useEffect(() => {
    if (!IS_ANDROID_APP || activeTab !== "account") return;
    setExternalLinkStatus("checking");  // never show the previous check's answer while re-checking
    let cancelled = false;
    resolveAndroidLinkStatus(
      {
        us: { available: Capacitor.isPluginAvailable("ExternalContentLink"), check: () => ExternalContentLink.checkEligibility() },
        eu: { available: Capacitor.isPluginAvailable("ExternalOffer"), check: () => ExternalOffer.checkEligibility() },
      },
      (promise) => withTimeout(promise, NATIVE_CHECK_TIMEOUT_MS),
    ).then((status) => { if (!cancelled) setExternalLinkStatus(status); });
    return () => { cancelled = true; };
  }, [activeTab]);

  // iOS app: purchase links on the US App Store storefront, and on EU storefronts by age
  // (euLinkStatus), checked each time the plans screen opens. No storefront, an old build without
  // the plugins, or an error all count as no.
  const signedInUserId = session?.user?.id ?? null;
  // The plans and the manage link: anywhere a purchase path exists.
  const purchaseOptionsShown = ["web", "eligible", "eligible_eu", "eu", "eu_gated"].includes(externalLinkStatus);
  useEffect(() => {
    if (!IS_IOS_APP || activeTab !== "account") return;
    if (!Capacitor.isPluginAvailable("AppStoreRegion")) {
      setExternalLinkStatus("ineligible");
      return;
    }
    // Never show the previous check's answer while re-checking (e.g. after signing in as someone
    // whose age doesn't allow purchases).
    setExternalLinkStatus("checking");
    let cancelled = false;
    withTimeout(AppStoreRegion.getStorefront(), NATIVE_CHECK_TIMEOUT_MS)
      .then((r) => (r?.countryCode === "USA" ? "web" : withTimeout(euLinkStatus(r?.countryCode, signedInUserId), NATIVE_CHECK_TIMEOUT_MS)))
      .then((status) => { if (!cancelled) setExternalLinkStatus(status); })
      .catch(() => { if (!cancelled) setExternalLinkStatus("ineligible"); });
    return () => { cancelled = true; };
  }, [activeTab, signedInUserId, euRecheck]);
  // Inside the app but without a usable bridge (neither platform detected): no purchase path.
  useEffect(() => {
    if (IN_APP_SHELL && !IS_IOS_APP && !IS_ANDROID_APP) setExternalLinkStatus("ineligible");
  }, []);

  // iOS app on an EU storefront: request Apple's tokens once per signed-in user per app session
  // (again before each purchase, in euBeforePurchase). Silent; failures are only logged.
  useEffect(() => {
    if (!IS_IOS_APP || !signedInUserId || euTokensRequestedFor.current === signedInUserId) return;
    if (!Capacitor.isPluginAvailable("AppStoreRegion") || !Capacitor.isPluginAvailable("EUExternalPurchase")) return;
    euTokensRequestedFor.current = signedInUserId;
    AppStoreRegion.getStorefront()
      .then((r) => (isEUStorefront(r?.countryCode) ? requestEUTokens() : null))
      .catch((e) => console.error("EU token request error:", e));
  }, [signedInUserId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      setPaymentSuccessMessage(true);
      setActiveTab("account");
      params.delete("payment");
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash
      );
      const timer = setTimeout(() => setPaymentSuccessMessage(false), 4000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Async flow recovery: if a job was still queued/running when the page was closed or
  // reloaded, pick it back up. Answers already submitted -> straight to the waiting screen;
  // otherwise re-ask the questionnaire while the job keeps running.
  useEffect(() => {
    if (!session || resumeChecked.current) return;
    resumeChecked.current = true;
    const pending = loadPendingJob(session.user.id);
    if (!pending) return;
    analysisResult.current = null;
    setErrorMessage(null);
    trackAsyncJob(pending.jobId);
    if (pending.answers) {
      setStatus("waiting");
      const interval = setInterval(() => {
        if (analysisResult.current) {
          clearInterval(interval);
          finishAnalysis(pending.answers, analysisResult.current);
        }
      }, 1000);
    } else {
      setStatus("questionnaire");
      setCurrentQuestion(0);
      setAnswers({});
      setCurrentAnswer("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    if (session && activeTab === "history") {
      loadSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, activeTab]);

  const loadSessions = async () => {
    setHistoryLoading(true);
    const { data, error } = await supabase
      .from("sessions")
      .select("*")
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: false });
    if (!error) setSessions(data);
    setHistoryLoading(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setResetError("Password must be at least 6 characters.");
      return;
    }
    setResetLoading(true);
    setResetError("");

    const code = new URLSearchParams(window.location.search).get("code");
    if (code && typeof supabase.auth.exchangeCodeForSession === "function") {
      const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
      if (exchangeError) {
        setResetError("Something went wrong. Please request a new reset link.");
        setResetLoading(false);
        return;
      }
    }

    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (!currentSession) {
      setResetError("Something went wrong. Please request a new reset link.");
      setResetLoading(false);
      return;
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setResetError("Something went wrong. Please request a new reset link.");
    } else {
      setIsRecovery(false);
      setNewPassword("");
      setConfirmPassword("");
    }
    setResetLoading(false);
  };

  const handleForgotPassword = async () => {
    setAuthLoading(true);
    setAuthError("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: "https://flowline.ski",
    });
    if (error) {
      setAuthError(error.message);
    } else {
      setAuthError("Check your email for a password reset link!");
    }
    setAuthLoading(false);
  };
  const handleLogin = async () => {
    setAuthLoading(true);
    setAuthError("");
    setAccountNotice("");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setAuthError(error.message);
    setAuthLoading(false);
  };

  const handleSignup = async () => {
    // Signup asks for no age data (App Review 5.1.1(v)): nothing in the app needs it except the iOS
    // EU purchase links, which ask for the birth year on the plans screen. The minimum age is
    // confirmed by agreeing to the Terms of Use (the line under the form).
    if (!fullName || !skiingLevel) {
      setAuthError("Please enter your name and skiing level.");
      return;
    }
    setAuthLoading(true);
    setAuthError("");
    // Kept in the auth user's metadata: there's no session before email confirmation, so the
    // profile can't be written here. /sync-signup-profile copies them after the first login.
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName.trim(),
          skiing_level: skiingLevel,
        },
      },
    });
    if (error) {
      setAuthError(error.message);
      setAuthLoading(false);
      return;
    }
    setAuthLoading(false);
    setAuthError("Account created! Please check your email to confirm your account before logging in.");
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setMenuOpen(false);
  };

  // Deletes the account through the backend (subscriptions cancelled, data, files and login
  // deleted), then signs out locally: the server-side sessions went with the user.
  const handleDeleteAccount = async () => {
    if (deleteText !== "DELETE" || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/delete-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${currentSession?.access_token}` },
        body: JSON.stringify({ confirm: deleteText }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.deleted !== true) {
        setDeleteError(data.message || "Your account couldn't be deleted. Please try again.");
        setDeleteBusy(false);
        return;
      }
      setDeleteOpen(false);
      setDeleteText("");
      setDeleteBusy(false);
      setActiveTab("analyze");
      setAccountNotice("Your account and all your data have been deleted.");
      await supabase.auth.signOut({ scope: "local" });
    } catch (e) {
      setDeleteError("Your account couldn't be deleted. Check your connection and try again.");
      setDeleteBusy(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setStatus("idle");
    setFeedback([]);
    setRecommendedDrill(null);
    setRecommendedDrillVideo(null);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setStatus("questionnaire");
    setCurrentQuestion(0);
    setAnswers({});
    setCurrentAnswer("");
    analysisResult.current = null;
    submittedAnswers.current = null;
    setErrorMessage(null);

    // Single upload attempt; only the status polling retries.
    (async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession?.access_token) {
          analysisResult.current = { error: true, message: "Your session has expired. Please sign in again." };
          return;
        }
        const { jobId, result } = await enqueueAnalysis(API_URL, file, currentSession.access_token);
        if (!jobId) {
          analysisResult.current = result;
          return;
        }
        savePendingJob(currentSession.user.id, jobId, submittedAnswers.current);
        trackAsyncJob(jobId);
      } catch {
        analysisResult.current = {
          error: true,
          message: "We couldn't complete the analysis. Please check your connection and try again.",
        };
      }
    })();
  };

  // Poll a queued async job; the outcome lands in analysisResult.current like the sync response.
  const trackAsyncJob = async (jobId) => {
    const getToken = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      return currentSession?.access_token;
    };
    const { terminal, data } = await pollAnalysisJob(API_URL, jobId, getToken);
    // Not terminal (session expired, connection lost, timed out): keep the job id so a reload resumes it.
    analysisResult.current = terminal ? data : { ...data, _resumable: true };
  };

  const handleNextQuestion = () => {
    const q = QUESTIONS[currentQuestion];
    const updatedAnswers = { ...answers, [q.id]: currentAnswer };
    setAnswers(updatedAnswers);
    setCurrentAnswer("");

    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      submittedAnswers.current = updatedAnswers;
      savePendingAnswers(updatedAnswers);
      if (analysisResult.current) {
        finishAnalysis(updatedAnswers, analysisResult.current);
      } else {
        setStatus("waiting");
        const interval = setInterval(() => {
          if (analysisResult.current) {
            clearInterval(interval);
            finishAnalysis(updatedAnswers, analysisResult.current);
          }
        }, 1000);
      }
    }
  };

  const finishAnalysis = async (finalAnswers, data) => {
    if (!data._resumable) clearPendingJob();
    if (data._status === 403 && data.error === "limit_reached") {
      setLimitMessage(data.message);
      setStatus("error");
      return;
    }
    if (data.error || !data.feedback) {
      setErrorMessage(data.message || "Something went wrong analyzing your video. Please try again.");
      setStatus("error");
      return;
    }
    setFeedback(data.feedback);
    setTurns(data.turns_detected);
    setOverallScore(data.overall_score);
        setPhaseScores(data.phase_scores);
        setAngleScores(data.angle_scores);
    setRecommendedDrill(data.recommended_drill || null);
    setRecommendedDrillVideo(null);
    if (data.recommended_drill) {
      const { data: drillData } = await supabase
        .from("drills")
        .select("title, video_url")
        .eq("title", data.recommended_drill)
        .maybeSingle();
      setRecommendedDrillVideo(drillData || null);
    }
    setStatus("done");

    if (session) {
      await supabase.from("sessions").insert({
        user_id: session.user.id,
        turns_detected: data.turns_detected,
        feedback: data.feedback,
        feel_score: finalAnswers.feel_score ? parseInt(finalAnswers.feel_score) : null,
        feel_today: finalAnswers.feel_today || null,
        skis_used: finalAnswers.skis_used || null,
        snow_conditions: finalAnswers.snow_conditions || null,
        notes: finalAnswers.notes || null,
        overall_score: data.overall_score ?? null,
        phase_scores: data.phase_scores ?? null,
        angle_scores: data.angle_scores ?? null,
      });
    }
  };

  // The parental gate as a promise: true once answered correctly, false if cancelled.
  const askParentalGate = () =>
    new Promise((resolve) => setParentalGate({ question: makeGateQuestion(), typed: "", wrong: false, resolve }));

  const submitParentalGate = () => {
    if (!parentalGate) return;
    if (isGateAnswerCorrect(parentalGate.question, parentalGate.typed)) {
      parentalGate.resolve(true);
      setParentalGate(null);
    } else {
      setParentalGate({ ...parentalGate, question: makeGateQuestion(), typed: "", wrong: true });
    }
  };

  const cancelParentalGate = () => {
    if (!parentalGate) return;
    parentalGate.resolve(false);
    setParentalGate(null);
  };

  // iOS app on an EU storefront, before anything that leads to a Stripe page: the parental gate
  // (if "eu_gated"), fresh tokens for Apple, then Apple's notice. true only if the user continued.
  // Not EU: true, nothing happens.
  const euBeforePurchase = async () => {
    if (externalLinkStatus !== "eu" && externalLinkStatus !== "eu_gated") return true;
    if (externalLinkStatus === "eu_gated" && !(await askParentalGate())) return false;
    try {
      await requestEUTokens();
      const notice = await EUExternalPurchase.showNotice({ type: "browser" });
      return notice?.continued === true;
    } catch (e) {
      console.error("EU external purchase error:", e);
      return false;
    }
  };

  // iOS app on an EU storefront, no birth year on file: stores the year from the plans screen's
  // prompt (POST /update-birth-year, which also refuses under 13), then re-runs the storefront/age
  // check, so the purchase path is decided exactly as for a year given at signup.
  const submitEUBirthYear = async () => {
    if (!euBirthYear || euBirthYearSaving) return;
    setEuBirthYearSaving(true);
    setEuBirthYearError("");
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/update-birth-year`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${currentSession?.access_token}` },
        body: JSON.stringify({ birth_year: Number(euBirthYear) }),
      });
      const data = await res.json().catch(() => ({}));
      // Already on file (e.g. saved from another device meanwhile): the re-check reads that one.
      if (res.ok || data.error === "birth_year_already_set") {
        setEuRecheck((n) => n + 1);
      } else {
        setEuBirthYearError(data.message || "Your birth year couldn't be saved. Please try again.");
      }
    } catch (e) {
      setEuBirthYearError("Your birth year couldn't be saved. Please try again.");
    } finally {
      setEuBirthYearSaving(false);
    }
  };

  const handleCheckout = async (priceId) => {
    if (!(await euBeforePurchase())) return;
    try {
      const res = await fetch(`${API_URL}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
        // Android via Play's external content link (US) or external offer (EEA): tagged with the
        // program, so the purchase is reported to Google under it.
        body: JSON.stringify({
          price_id: priceId,
          ...(IS_ANDROID_APP && androidLinkChannel(externalLinkStatus) ? { channel: androidLinkChannel(externalLinkStatus) } : {}),
        }),
      });
      const data = await res.json();
      // The server refuses a second subscription; plan changes happen in the Stripe portal.
      // (euBeforePurchase already ran for this tap.)
      if (data.error === "already_subscribed") return openSubscriptionPortal();
      if (!data.url) alert("No URL returned: " + JSON.stringify(data));
      if (data.url) await openPaymentUrl(data.url, data.session_id);
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  // Browsers and the iOS app on the US storefront navigate to the Stripe page, and so does the iOS
  // app on an EU storefront (after euBeforePurchase); the iOS app elsewhere doesn't. The Android app
  // may only leave through Play's launchExternalLink (which shows Google's information screen
  // first), and only when eligible: the US program's plugin or the EEA one's, never both.
  // checkoutSessionId: for a Stripe checkout, so Play's token for this link-out can be reported
  // with that purchase.
  const openPaymentUrl = async (url, checkoutSessionId) => {
    if (IS_IOS_APP && !["web", "eu", "eu_gated"].includes(externalLinkStatus)) return;
    if (!IS_ANDROID_APP) {
      window.location.href = url;
      return;
    }
    const linkPlugin = externalLinkStatus === "eligible" ? ExternalContentLink
      : externalLinkStatus === "eligible_eu" ? ExternalOffer : null;
    if (!linkPlugin) return;
    const result = await linkPlugin.launch({ url });
    if (result?.launched && result?.externalTransactionToken && checkoutSessionId) {
      storePendingLinkToken(pendingLinkToken, { token: result.externalTransactionToken, checkoutSessionId });
      sendPendingLinkToken(pendingLinkToken);
    }
    if (result?.launched) awaitingExternalReturn.current = true;
  };

  const openSubscriptionPortal = async () => {
    try {
      const res = await fetch(`${API_URL}/create-portal-session`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (data.url) await openPaymentUrl(data.url);
    } catch (err) {}
  };

  const handleManageSubscription = async () => {
    if (!(await euBeforePurchase())) return;  // the Stripe portal can change plans and payments
    await openSubscriptionPortal();
  };

  // Android app without Play's external content links: a portal limited to cancelling,
  // immediately (no invoices, plans or payment changes), so it has no payment path. Opened as an ordinary
  // link: Capacitor hands navigation to another host to the system browser.
  const handleRestrictedManageSubscription = async () => {
    try {
      const res = await fetch(`${API_URL}/create-portal-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ restricted: true }),
      });
      const data = await res.json();
      // Only a session the backend confirms as restricted; an older backend would return the
      // full portal (plan changes, payments) for the same request.
      if (!data.url || data.restricted !== true) {
        alert(data.message || "Subscription management isn't available right now.");
        return;
      }
      awaitingExternalReturn.current = true;
      window.location.href = data.url;
    } catch (err) {
      alert("Subscription management isn't available right now.");
    }
  };

  const navigateTo = (tab) => {
    setActiveTab(tab);
    setMenuOpen(false);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <img src="/logo.png" alt="FlowLine" style={styles.logo} />
        </div>
      </div>
    );
  }

  if (isRecovery) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <img src="/logo.png" alt="FlowLine" style={styles.logo} />
          <p style={styles.subtitle}>Your AI Ski Coach</p>
        </div>
        <div style={styles.card}>
          <h2 style={{ color: "#c9a84c", fontWeight: 700, fontSize: 20, marginTop: 0, marginBottom: 4 }}>
            Reset Password
          </h2>
          <p style={{ color: "#8899aa", fontSize: 13, marginTop: 0, marginBottom: 20 }}>
            Enter your new password below.
          </p>
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={styles.input}
          />
          <input
            type="password"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
            style={styles.input}
          />
          {resetError && <p style={styles.errorText}>{resetError}</p>}
          <button
            style={{ ...styles.button, opacity: resetLoading ? 0.5 : 1 }}
            onClick={handleResetPassword}
            disabled={resetLoading}
          >
            {resetLoading ? "Updating..." : "Set New Password"}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!session) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <img src="/logo.png" alt="FlowLine" style={styles.logo} />
          <p style={styles.subtitle}>Your AI Ski Coach</p>
        </div>
        {accountNotice && (
          <div style={{ ...styles.card, borderColor: "#c9a84c" }}>
            <p style={{ color: "#c9a84c", margin: 0 }}>{accountNotice}</p>
          </div>
        )}
        {authMode === "forgot" && (
          <div style={styles.card}>
            <p style={{ color: "#ddddee", marginTop: 0, marginBottom: 16 }}>
              Enter your email and we'll send you a reset link.
            </p>
            <input type="email" placeholder="Email" value={email}
              onChange={(e) => setEmail(e.target.value)} style={styles.input} />
            {authError && <p style={{ ...styles.errorText, color: authError.startsWith("Account created!") ? "#c9a84c" : "#ff4444" }}>{authError}</p>}
            <button style={{ ...styles.button, opacity: authLoading ? 0.5 : 1 }}
              onClick={handleForgotPassword} disabled={authLoading}>
              {authLoading ? "Sending..." : "Send Reset Link"}
            </button>
            <p style={{ fontSize: 13, color: "#8899aa", textAlign: "center", marginTop: 12, cursor: "pointer" }}
              onClick={() => { setAuthMode("login"); setAuthError(""); }}>
              Back to Log In
            </p>
          </div>
        )}
        <div style={styles.card}>
          <div style={styles.tabRow}>
            <button style={{ ...styles.tab, ...(authMode === "login" ? styles.tabActive : {}) }}
              onClick={() => { setAuthMode("login"); setAuthError(""); }}>Log In</button>
            <button style={{ ...styles.tab, ...(authMode === "signup" ? styles.tabActive : {}) }}
              onClick={() => { setAuthMode("signup"); setAuthError(""); }}>Sign Up</button>
          </div>
          <input type="email" placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)} style={styles.input} />
          <input type="password" placeholder="Password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && authMode === "login" && handleLogin()}
            style={styles.input} />
          {authMode === "signup" && (
            <>
              <input type="text" placeholder="Full Name" value={fullName}
                onChange={(e) => setFullName(e.target.value)} style={styles.input} />
              <select value={skiingLevel} onChange={(e) => setSkiingLevel(e.target.value)} style={styles.input}>
                <option value="">Select Skiing Level</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Expert">Expert</option>
                <option value="Racer">Racer</option>
              </select>
              {/* Opens in a new tab so the half-filled form isn't lost. */}
              <p style={{ fontSize: 13, color: "#8899aa", marginTop: 0, marginBottom: 12 }}>
                By creating an account you agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>Terms of Use</a>
              </p>
            </>
          )}
         {authMode === "login" && (
            <p
              style={{ fontSize: 13, color: "#8899aa", textAlign: "right", marginBottom: 12, cursor: "pointer" }}
              onClick={() => setAuthMode("forgot")}
            >
              Forgot password?
            </p>
          )}
          {authError && <p style={{ ...styles.errorText, color: authError.startsWith("Account created!") ? "#c9a84c" : "#ff4444" }}>{authError}</p>}
          <button style={{ ...styles.button, opacity: authLoading ? 0.5 : 1 }}
           onClick={authMode === "login" ? handleLogin : authMode === "signup" ? handleSignup : handleForgotPassword}
            disabled={authLoading}>
            {authLoading ? "Please wait..." : authMode === "login" ? "Log In" : "Create Account"}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  if (status === "questionnaire") {
    const q = QUESTIONS[currentQuestion];
    const progress = (currentQuestion / QUESTIONS.length) * 100;
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <img src="/logo.png" alt="FlowLine" style={styles.logo} />
        </div>
        <div style={styles.card}>
          <p style={styles.processingBanner}>
            🎿 Your video is being analyzed. Take your time to answer a few questions about your run in the meantime.
          </p>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${progress}%` }} />
          </div>
          <p style={styles.progressText}>{currentQuestion + 1} of {QUESTIONS.length}</p>
          <h2 style={styles.questionText}>{q.question}</h2>
          {q.type === "slider" && (
            <div style={styles.sliderContainer}>
              <input type="range" min={q.min} max={q.max}
                value={currentAnswer || 5}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                style={styles.slider} />
              <p style={styles.sliderValue}>{currentAnswer || 5} / 10</p>
            </div>
          )}
          {q.type === "choice" && (
            <div style={styles.choiceContainer}>
              {q.options.map((opt) => (
                <button key={opt}
                  style={{ ...styles.choiceButton, ...(currentAnswer === opt ? styles.choiceButtonActive : {}) }}
                  onClick={() => setCurrentAnswer(opt)}>{opt}</button>
              ))}
            </div>
          )}
          {q.type === "text" && (
            <input type="text" placeholder={q.placeholder} value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)} style={styles.input} />
          )}
          <button style={{ ...styles.button, marginTop: 20 }} onClick={handleNextQuestion}>
            {currentQuestion < QUESTIONS.length - 1 ? "Next →" : "Submit"}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  if (status === "waiting") {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <img src="/logo.png" alt="FlowLine" style={styles.logo} />
        </div>
        <div style={styles.card}>
          <p style={styles.processingBanner}>
            ⏳ Almost there — your video is still being analyzed. Hang tight!
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={styles.container}>

      {paymentSuccessMessage && (
        <div style={{ textAlign: "center", padding: "12px 16px", color: "#c9a84c", fontWeight: 700, fontSize: 15 }}>
          Payment successful! Welcome to FlowLine.
        </div>
      )}

      {/* SLIDE-OUT MENU OVERLAY */}
      {menuOpen && (
        <div style={styles.overlay} onClick={() => setMenuOpen(false)}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.drawerHeader}>
              <img src="/logo.png" alt="FlowLine" style={styles.drawerLogo} />
            </div>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                style={{
                  ...styles.drawerItem,
                  ...(activeTab === item.id ? styles.drawerItemActive : {}),
                }}
                onClick={() => navigateTo(item.id)}
              >
                <span style={styles.drawerIcon}>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
            {INFO_LINKS.map((link) => (
              <a key={link.href} href={link.href} style={{ ...styles.drawerItem, ...styles.drawerLink }}>
                <span style={styles.drawerIcon}>{link.icon}</span>
                <span>{link.label}</span>
              </a>
            ))}
            <div style={styles.drawerDivider} />
            <button style={styles.drawerLogout} onClick={handleLogout}>
              Log Out
            </button>
          </div>
        </div>
      )}

      {/* TOP BAR */}
      <div style={styles.topBar}>
        <button style={styles.hamburger} onClick={() => setMenuOpen(true)}>
          ☰
        </button>
        <img src="/logo.png" alt="FlowLine" style={styles.topBarLogo} />
        <div style={{ width: 40 }} />
      </div>

      {/* ANALYZE TAB */}
      {activeTab === "analyze" && (
        <>
          <div style={styles.card}>
            <label style={styles.uploadLabel}>
              <div style={styles.uploadBox}>
                {file ? (
                  <>
                    <div style={styles.uploadIcon}>✅</div>
                    <p style={styles.uploadText}>{file.name}</p>
                    <p style={styles.uploadHint}>Tap to change video</p>
                  </>
                ) : (
                  <>
                    <div style={styles.uploadIcon}>🎥</div>
                    <p style={styles.uploadText}>Tap to upload your ski video</p>
                    <p style={styles.uploadHint}>MOV or MP4 from your phone</p>
                  </>
                )}
              </div>
              <input type="file" accept="video/*" onChange={handleFileChange} style={{ display: "none" }} />
            </label>
            <button style={{ ...styles.button, opacity: !file ? 0.5 : 1 }}
              onClick={handleAnalyze} disabled={!file}>
              Analyze My Skiing
            </button>
            {status === "error" && limitMessage ? (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <p style={styles.errorText}>{limitMessage}</p>
                <button style={styles.button} onClick={() => setActiveTab("account")}>
                  Upgrade Plan
                </button>
              </div>
            ) : status === "error" && (
              <p style={styles.errorText}>{errorMessage || "Something went wrong. Please try again."}</p>
            )}
          </div>
          {status === "done" && (
            <div style={styles.card}>
              {/* Overall Score */}
          {overallScore && (
            <div style={styles.scoreContainer}>
              <p style={styles.overallScoreLabel}>Overall Score</p>
              <p style={styles.overallScoreValue}>{overallScore}<span style={styles.overallScoreMax}>/100</span></p>
            </div>
          )}

          {/* Phase Scores */}
          {phaseScores && (
            <div style={styles.phaseScoreRow}>
              {Object.entries(phaseScores).map(([phase, score]) => (
                <div key={phase} style={styles.phaseScoreBox}>
                  <p style={styles.phaseScoreValue}>{score}</p>
                  <p style={styles.phaseScoreLabel}>{phase}</p>
                </div>
              ))}
            </div>
          )}

          {/* Angle Scores */}
          {angleScores && (
            <div style={{ marginBottom: 16 }}>
              <p style={styles.sectionSubtitle}>Technique Breakdown</p>
              {Object.entries(angleScores).map(([angle, score]) => (
                <div key={angle} style={styles.angleRow}>
                  <p style={styles.angleName}>{angle}</p>
                  <div style={styles.angleBarBg}>
                    <div style={{ ...styles.angleBarFill, width: `${(score / 10) * 100}%`, backgroundColor: score >= 7 ? "#c9a84c" : score >= 4 ? "#f0a500" : "#ff4444" }} />
                  </div>
                  <p style={styles.angleScore}>{score}</p>
                </div>
              ))}
            </div>
          )}

          <h2 style={styles.feedbackTitle}>Your Feedback</h2>
          {feedback.map((msg, i) => (
            <div key={i} style={styles.feedbackItem}>
              <span style={styles.feedbackBullet}>▸</span>
              <p style={styles.feedbackText}>{msg}</p>
            </div>
          ))}
            </div>
          )}

          {status === "done" && fileUrl && (
            <div style={styles.card}>
              <p style={{ color: "#c9a84c", fontWeight: "700", fontSize: 13, textTransform: "uppercase", letterSpacing: 1, margin: "0 0 8px 0" }}>Your Run</p>
              <video
                src={fileUrl}
                controls
                playsInline
                ref={(el) => { if (el) el.playbackRate = 0.5; }}
                style={{ width: "100%", borderRadius: 12, display: "block" }}
              />
            </div>
          )}

          {status === "done" && recommendedDrill && (
            <div style={{ backgroundColor: "#112236", borderRadius: 16, padding: 20, marginBottom: 16, border: "1px solid #c9a84c44" }}>
              <p style={{ fontSize: 22, margin: "0 0 8px 0" }}>🎬</p>
              <p style={{ color: "#c9a84c", fontWeight: "700", fontSize: 13, textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px 0" }}>Recommended Drill</p>
              <p style={{ color: "#ffffff", fontSize: 16, fontWeight: "600", margin: 0 }}>{recommendedDrill}</p>
              {recommendedDrillVideo?.video_url && (
                <video
                  key={recommendedDrillVideo.video_url}
                  controls
                  playsInline
                  style={{ width: "100%", borderRadius: 12, display: "block", marginTop: 12, backgroundColor: "#000" }}
                >
                  <source src={recommendedDrillVideo.video_url} type="video/mp4" />
                </video>
              )}
            </div>
          )}
        </>
      )}

      {/* HISTORY TAB */}
      {activeTab === "history" && (
        <div>
          <h2 style={styles.sectionTitle}>Your Sessions</h2>
          {subscriptionStatus === "free" ? (
            <div style={{ ...styles.card, textAlign: "center" }}>
              <p style={{ fontSize: 22, margin: "0 0 8px 0" }}>📋</p>
              <p style={{ color: "#ffffff", fontSize: 15, margin: "0 0 16px 0" }}>
                Session history is available on Basic and Premium plans.
              </p>
              <button style={styles.button} onClick={() => setActiveTab("account")}>
                Upgrade
              </button>
            </div>
          ) : (
          <>
          {historyLoading && <p style={styles.emptyText}>Loading...</p>}
          {!historyLoading && sessions.length === 0 && (
            <p style={styles.emptyText}>No sessions yet. Analyze your first run!</p>
          )}
          {sessions.map((s) => (
            <div key={s.id} style={styles.sessionCard}
              onClick={() => setExpandedSession(expandedSession === s.id ? null : s.id)}>
              <div style={styles.sessionHeader}>
                <div>
                  <p style={styles.sessionDate}>{formatDate(s.created_at)}</p>
                  <p style={styles.sessionTime}>{formatTime(s.created_at)}</p>
                </div>
                <div style={styles.sessionMeta}>
                  {s.feel_score && <span style={styles.sessionBadge}>😊 {s.feel_score}/10</span>}
                  {s.overall_score != null && <span style={styles.sessionBadge}>⭐ {s.overall_score}/100</span>}
                  {s.snow_conditions && <span style={styles.sessionBadge}>{s.snow_conditions}</span>}
                </div>
              </div>
              {expandedSession === s.id && (
                <div style={styles.sessionExpanded}>
                  <div style={styles.sessionDivider} />
                  {s.skis_used && <p style={styles.sessionDetail}>🎿 <strong>Skis:</strong> {s.skis_used}</p>}
                  {s.feel_today && <p style={styles.sessionDetail}>💪 <strong>Feeling:</strong> {s.feel_today}</p>}
                  {s.notes && <p style={styles.sessionDetail}>📝 <strong>Notes:</strong> {s.notes}</p>}
                  {s.angle_scores && Object.keys(s.angle_scores).length > 0 && (
                    <>
                      <p style={{ ...styles.sessionFeedbackTitle, marginTop: s.skis_used || s.feel_today || s.notes ? 12 : 0 }}>Technique Breakdown</p>
                      {Object.entries(s.angle_scores).map(([angle, score]) => (
                        <div key={angle} style={styles.angleRow}>
                          <p style={styles.angleName}>{angle}</p>
                          <div style={styles.angleBarBg}>
                            <div style={{ ...styles.angleBarFill, width: `${(score / 10) * 100}%`, backgroundColor: score >= 7 ? "#c9a84c" : score >= 4 ? "#f0a500" : "#ff4444" }} />
                          </div>
                          <p style={styles.angleScore}>{score}</p>
                        </div>
                      ))}
                    </>
                  )}
                  {s.feedback && s.feedback.length > 0 && (
                    <>
                      <p style={styles.sessionFeedbackTitle}>Feedback</p>
                      {s.feedback.map((msg, i) => (
                        <div key={i} style={styles.feedbackItem}>
                          <span style={styles.feedbackBullet}>▸</span>
                          <p style={styles.feedbackText}>{msg}</p>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
          </>
          )}
        </div>
      )}

{/* DRILLS TAB */}
      {activeTab === "drills" && (
        <DrillsTab supabase={supabase} subscriptionStatus={subscriptionStatus} onUpgrade={() => setActiveTab("account")} />
      )}

      {/* HOW IT WORKS TAB */}
      {activeTab === "howItWorks" && (
        <div>
          <h2 style={styles.sectionTitle}>How It Works</h2>

          <div style={styles.card}>
            <p style={styles.howStep}>1️⃣ &nbsp;<strong>Film your run</strong></p>
            <p style={styles.howText}>
              Have someone film you skiing from the front. A full run of 5–15 seconds works best. Use your phone camera in normal video mode.
            </p>
          </div>

          <div style={styles.card}>
            <p style={styles.howStep}>2️⃣ &nbsp;<strong>Upload your video</strong></p>
            <p style={styles.howText}>
              Go to the Analyze tab and upload your video. FlowLine accepts MOV and MP4 files directly from your phone.
            </p>
          </div>

          <div style={styles.card}>
            <p style={styles.howStep}>3️⃣ &nbsp;<strong>AI analyzes your technique</strong></p>
            <p style={styles.howText}>
              FlowLine uses AI pose detection to track your body position frame by frame. It detects every turn, identifies the initiation, apex, and transition phases, and measures key angles, including knees, hips, arms, and shoulders.
            </p>
          </div>

          <div style={styles.card}>
            <p style={styles.howStep}>4️⃣ &nbsp;<strong>Get personalized feedback</strong></p>
            <p style={styles.howText}>
              Based on what the AI detects, you receive specific, actionable feedback on what to improve — just like having a top-level coach watching your run.
            </p>
          </div>

          <div style={styles.card}>
            <p style={styles.howStep}>5️⃣ &nbsp;<strong>Track your progress</strong></p>
            <p style={styles.howText}>
              Every session is saved in your History. Over time you can see how your technique evolves and which areas you've improved.
            </p>
          </div>

          <div style={{ ...styles.card, borderColor: "#c9a84c44" }}>
            <p style={styles.howStep}>🏔️ &nbsp;<strong>Built by a racer, for skiers</strong></p>
            <p style={styles.howText}>
              FlowLine was created by a competitive ski racer who reached the top 250 in the world. The feedback logic is based on real coaching principles used at the highest level of the sport.
            </p>
          </div>
        </div>
      )}

      {/* ACCOUNT TAB */}
      {activeTab === "account" && (
        <div>
          <h2 style={styles.sectionTitle}>Account</h2>

          <div style={styles.card}>
            <p style={{ fontSize: 12, color: "#8899aa", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 6px 0" }}>Current Plan</p>
            <p style={{ fontSize: 22, fontWeight: 700, color: "#c9a84c", margin: 0 }}>
              {subscriptionStatus === "premium" ? "Premium" : subscriptionStatus === "basic" ? "Basic" : "Free"}
            </p>
            <p style={{ fontSize: 13, color: "#8899aa", margin: "6px 0 0 0" }}>
              {session?.user?.email}
            </p>
          </div>

          {externalLinkStatus === "checking" && (
            <p style={{ fontSize: 13, color: "#8899aa", textAlign: "center", margin: "8px 0 16px 0" }}>Loading plans…</p>
          )}
          {/* Not eligible for Play's external content links (US) or external offers (EEA): no
              purchase path, no link, and no hint to buy elsewhere (Play policy). */}
          {/* iOS app on an EU storefront, no birth year on file: ask for it here (optional at signup). */}
          {externalLinkStatus === "eu_needs_birth_year" && (
            <div style={styles.card}>
              <p style={{ fontSize: 15, color: "#ffffff", margin: "0 0 4px 0" }}>Your birth year</p>
              <p style={{ fontSize: 13, color: "#8899aa", margin: "0 0 12px 0" }}>Required by EU law for this purchase option.</p>
              <select value={euBirthYear} onChange={(e) => setEuBirthYear(e.target.value)} style={styles.input}>
                <option value="">Select Birth Year</option>
                {BIRTH_YEARS.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {euBirthYearError && <p style={{ ...styles.errorText, marginTop: 0 }}>{euBirthYearError}</p>}
              <button
                style={{ ...styles.button, opacity: !euBirthYear || euBirthYearSaving ? 0.5 : 1 }}
                disabled={!euBirthYear || euBirthYearSaving}
                onClick={submitEUBirthYear}
              >
                {euBirthYearSaving ? "Saving…" : "Continue"}
              </button>
            </div>
          )}
          {externalLinkStatus === "ineligible" && (
            <div style={styles.card}>
              <p style={{ fontSize: 15, color: "#ffffff", margin: 0 }}>In-app upgrades aren't available in your region yet.</p>
            </div>
          )}
          {/* Android only for now: unconfirmed whether Apple treats a cancel-only link the same way. */}
          {IS_ANDROID_APP && externalLinkStatus === "ineligible" && (subscriptionStatus === "basic" || subscriptionStatus === "premium") && (
            <p
              style={{ textAlign: "center", color: "#8899aa", fontSize: 13, margin: "0 0 16px 0", cursor: "pointer", textDecoration: "underline" }}
              onClick={handleRestrictedManageSubscription}
            >
              Manage subscription
            </p>
          )}
          {purchaseOptionsShown && (
            <>
            <p style={{ fontSize: 13, color: "#8899aa", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, marginTop: 4 }}>
              Upgrade Your Plan
            </p>

            {/* Basic */}
            <div style={{ ...styles.card, borderColor: subscriptionStatus === "basic" ? "#c9a84c" : "#1e3a5f" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <p style={{ fontSize: 18, fontWeight: 700, color: "#ffffff", margin: "0 0 4px 0" }}>Basic</p>
                  <p style={{ fontSize: 13, color: "#8899aa", margin: 0 }}>3 analyses per week · History tracking</p>
                </div>
                <p style={{ fontSize: 20, fontWeight: 700, color: "#c9a84c", margin: 0, flexShrink: 0 }}>€15<span style={{ fontSize: 13, color: "#8899aa" }}>/mo</span></p>
              </div>
              <button
                style={{ ...styles.button, opacity: subscriptionStatus === "basic" ? 0.4 : 1 }}
                disabled={subscriptionStatus === "basic"}
                onClick={subscriptionStatus === "premium" ? handleManageSubscription : () => handleCheckout("price_1USQFtIMSYtZtaWroU2VwHRS")}
              >
                {subscriptionStatus === "basic" ? "Current Plan" : subscriptionStatus === "premium" ? "Change plan" : "Upgrade to Basic"}
              </button>
            </div>

            {/* Premium */}
            <div style={{ ...styles.card, borderColor: subscriptionStatus === "premium" ? "#c9a84c" : "#1e3a5f" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <p style={{ fontSize: 18, fontWeight: 700, color: "#ffffff", margin: "0 0 4px 0" }}>
                    Premium <span style={{ fontSize: 12, backgroundColor: "#c9a84c22", color: "#c9a84c", borderRadius: 99, padding: "2px 8px" }}>Best Value</span>
                  </p>
                  <p style={{ fontSize: 13, color: "#8899aa", margin: 0 }}>Everything in Basic · Unlimited analyses · Full drill library</p>
                </div>
                <p style={{ fontSize: 20, fontWeight: 700, color: "#c9a84c", margin: 0, flexShrink: 0 }}>€25<span style={{ fontSize: 13, color: "#8899aa" }}>/mo</span></p>
              </div>
              <button
                style={{ ...styles.button, opacity: subscriptionStatus === "premium" ? 0.4 : 1 }}
                disabled={subscriptionStatus === "premium"}
                onClick={subscriptionStatus === "basic" ? handleManageSubscription : () => handleCheckout("price_1USQFtIMSYtZtaWr4fyhLtIU")}
              >
                {subscriptionStatus === "premium" ? "Current Plan" : subscriptionStatus === "basic" ? "Change plan" : "Upgrade to Premium"}
              </button>
            </div>

            {/* Single Analysis */}
            <div style={styles.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <p style={{ fontSize: 18, fontWeight: 700, color: "#ffffff", margin: "0 0 4px 0" }}>Single Analysis</p>
                  <p style={{ fontSize: 13, color: "#8899aa", margin: 0 }}>One-time · No subscription needed</p>
                </div>
                <p style={{ fontSize: 20, fontWeight: 700, color: "#c9a84c", margin: 0, flexShrink: 0 }}>€10<span style={{ fontSize: 13, color: "#8899aa" }}> once</span></p>
              </div>
              <button
                style={styles.button}
                onClick={() => handleCheckout("price_1USQFtIMSYtZtaWrW0nV4d7j")}
              >
                Buy Single Analysis
              </button>
            </div>
            </>
          )}

          {/* EU, under 18: a parent answers before Apple's notice and the Stripe page. */}
          {parentalGate && (
            <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
              <div style={{ ...styles.card, maxWidth: 360, width: "100%", margin: 0 }}>
                <p style={{ fontSize: 18, fontWeight: 700, color: "#ffffff", margin: "0 0 8px 0" }}>Ask a parent</p>
                <p style={{ fontSize: 14, color: "#8899aa", margin: "0 0 16px 0" }}>
                  A parent or guardian needs to continue. What is {parentalGate.question.a} + {parentalGate.question.b}?
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  value={parentalGate.typed}
                  onChange={(e) => setParentalGate({ ...parentalGate, typed: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && submitParentalGate()}
                  style={styles.input}
                />
                {parentalGate.wrong && <p style={{ ...styles.errorText, color: "#ff4444" }}>That's not right. Try this one.</p>}
                <button style={styles.button} onClick={submitParentalGate}>Continue</button>
                <p style={{ fontSize: 13, color: "#8899aa", textAlign: "center", marginTop: 12, cursor: "pointer" }} onClick={cancelParentalGate}>
                  Cancel
                </p>
              </div>
            </div>
          )}

          <div style={styles.drawerDivider} />
          <button style={{ ...styles.button, backgroundColor: "transparent", border: "1px solid #1e3a5f", color: "#8899aa" }} onClick={handleLogout}>
            Log Out
          </button>

          {(subscriptionStatus === "basic" || subscriptionStatus === "premium") &&
            purchaseOptionsShown && (
            <p
              style={{ textAlign: "center", color: "#8899aa", fontSize: 12, marginTop: 12, cursor: "pointer", textDecoration: "underline" }}
              onClick={handleManageSubscription}
            >
              Manage or cancel subscription
            </p>
          )}

          {!deleteOpen ? (
            <p
              style={{ textAlign: "center", color: "#8899aa", fontSize: 12, marginTop: 24, cursor: "pointer", textDecoration: "underline" }}
              onClick={() => { setDeleteOpen(true); setDeleteText(""); setDeleteError(""); }}
            >
              Delete my account
            </p>
          ) : (
            <div style={{ ...styles.card, borderColor: "#ff4444", marginTop: 24 }}>
              <p style={{ fontSize: 16, fontWeight: 700, color: "#ff4444", margin: "0 0 8px 0" }}>Delete my account</p>
              <p style={{ fontSize: 13, color: "#ddddee", margin: "0 0 8px 0" }}>
                This permanently deletes your account and can't be undone:
              </p>
              <ul style={{ fontSize: 13, color: "#ddddee", margin: "0 0 8px 0", paddingLeft: 20 }}>
                <li>your profile, analysis history and remaining single analyses</li>
                <li>your Social Feed posts, comments, likes and follows</li>
                <li>your Buddy Finder profile, photos, matches and messages</li>
                <li>your login</li>
              </ul>
              <p style={{ fontSize: 13, color: "#ddddee", margin: "0 0 12px 0" }}>
                Any active subscription is cancelled immediately. Records of past payments are kept by our payment provider, Stripe, as required for tax and accounting.
              </p>
              <p style={{ fontSize: 13, color: "#8899aa", margin: "0 0 6px 0" }}>Type DELETE to confirm.</p>
              <input
                type="text"
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                placeholder="DELETE"
                autoCapitalize="characters"
                autoComplete="off"
                style={styles.input}
                disabled={deleteBusy}
              />
              {deleteError && <p style={styles.errorText}>{deleteError}</p>}
              <button
                style={{ ...styles.button, backgroundColor: "#ff4444", color: "#ffffff", opacity: deleteText === "DELETE" && !deleteBusy ? 1 : 0.4 }}
                disabled={deleteText !== "DELETE" || deleteBusy}
                onClick={handleDeleteAccount}
              >
                {deleteBusy ? "Deleting..." : "Permanently delete my account"}
              </button>
              <button
                style={{ ...styles.button, backgroundColor: "transparent", border: "1px solid #1e3a5f", color: "#8899aa", marginTop: 8 }}
                disabled={deleteBusy}
                onClick={() => { setDeleteOpen(false); setDeleteText(""); setDeleteError(""); }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
}

const styles = {
  container: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "0 16px 40px 16px",
    fontFamily: "Georgia, 'Times New Roman', serif",
    backgroundColor: "#0d1b2a",
    minHeight: "100vh",
    color: "#ffffff",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 0",
    marginBottom: 16,
  },
  hamburger: {
    backgroundColor: "transparent",
    border: "none",
    color: "#c9a84c",
    fontSize: 24,
    cursor: "pointer",
    width: 40,
    padding: 0,
  },
  topBarLogo: {
    width: 120,
    height: "auto",
    borderRadius: 8,
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    zIndex: 200,
    display: "flex",
  },
  drawer: {
    width: 260,
    backgroundColor: "#0d1b2a",
    borderRight: "1px solid #1e3a5f",
    height: "100%",
    padding: "24px 0",
    display: "flex",
    flexDirection: "column",
  },
  drawerHeader: {
    padding: "0 20px 24px 20px",
    borderBottom: "1px solid #1e3a5f",
    marginBottom: 16,
  },
  drawerLogo: {
    width: 130,
    height: "auto",
    borderRadius: 8,
  },
  drawerItem: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "14px 20px",
    backgroundColor: "transparent",
    border: "none",
    color: "#ddddee",
    fontSize: 16,
    cursor: "pointer",
    fontFamily: "Georgia, serif",
    textAlign: "left",
    width: "100%",
  },
  drawerItemActive: {
    color: "#c9a84c",
    backgroundColor: "#112236",
  },
  drawerIcon: {
    fontSize: 20,
  },
  // A drawer item that's a link (to a page outside the app) rather than a button.
  drawerLink: {
    textDecoration: "none",
    boxSizing: "border-box",
  },
  drawerDivider: {
    height: 1,
    backgroundColor: "#1e3a5f",
    margin: "16px 0",
  },
  drawerLogout: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "14px 20px",
    backgroundColor: "transparent",
    border: "none",
    color: "#8899aa",
    fontSize: 16,
    cursor: "pointer",
    fontFamily: "Georgia, serif",
    textAlign: "left",
    width: "100%",
  },
  header: {
    textAlign: "center",
    marginBottom: 28,
    paddingTop: 24,
  },
  logo: {
    width: 200,
    height: "auto",
    borderRadius: 12,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    color: "#c9a84c",
    marginTop: 4,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontFamily: "Georgia, serif",
  },
  card: {
    backgroundColor: "#112236",
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    border: "1px solid #1e3a5f",
  },
  tabRow: {
    display: "flex",
    marginBottom: 16,
    gap: 8,
  },
  tab: {
    flex: 1,
    padding: "10px",
    backgroundColor: "transparent",
    color: "#8899aa",
    border: "1px solid #1e3a5f",
    borderRadius: 8,
    fontSize: 15,
    cursor: "pointer",
    fontFamily: "Georgia, serif",
  },
  tabActive: {
    backgroundColor: "#1e3a5f",
    color: "#c9a84c",
    border: "1px solid #c9a84c",
  },
  input: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#0d1b2a",
    color: "#ffffff",
    border: "1px solid #1e3a5f",
    borderRadius: 12,
    fontSize: 16,
    marginBottom: 12,
    boxSizing: "border-box",
    fontFamily: "Georgia, serif",
  },
  button: {
    width: "100%",
    padding: "16px",
    backgroundColor: "#c9a84c",
    color: "#0d1b2a",
    border: "none",
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.04em",
    fontFamily: "Georgia, serif",
  },
  uploadLabel: {
    cursor: "pointer",
    display: "block",
  },
  uploadBox: {
    border: "2px dashed #c9a84c44",
    borderRadius: 12,
    padding: "32px 16px",
    textAlign: "center",
    marginBottom: 16,
  },
  uploadIcon: {
    fontSize: 40,
    marginBottom: 8,
  },
  uploadText: {
    fontSize: 16,
    fontWeight: 600,
    color: "#ffffff",
    margin: "0 0 4px 0",
  },
  uploadHint: {
    fontSize: 13,
    color: "#8899aa",
    margin: 0,
  },
  processingBanner: {
    backgroundColor: "#0d1b2a",
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 14,
    color: "#c9a84c",
    marginBottom: 20,
    lineHeight: 1.6,
    textAlign: "center",
  },
  progressBar: {
    backgroundColor: "#1e3a5f",
    borderRadius: 99,
    height: 6,
    marginBottom: 6,
    overflow: "hidden",
  },
  progressFill: {
    backgroundColor: "#c9a84c",
    height: "100%",
    borderRadius: 99,
    transition: "width 0.3s ease",
  },
  progressText: {
    fontSize: 12,
    color: "#8899aa",
    textAlign: "right",
    marginBottom: 20,
  },
  questionText: {
    fontSize: 20,
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 20,
    marginTop: 0,
  },
  sliderContainer: {
    textAlign: "center",
  },
  slider: {
    width: "100%",
    accentColor: "#c9a84c",
  },
  sliderValue: {
    fontSize: 28,
    fontWeight: 700,
    color: "#c9a84c",
    marginTop: 8,
  },
  choiceContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  choiceButton: {
    padding: "14px",
    backgroundColor: "#0d1b2a",
    color: "#ffffff",
    border: "1px solid #1e3a5f",
    borderRadius: 12,
    fontSize: 16,
    cursor: "pointer",
    fontFamily: "Georgia, serif",
    textAlign: "left",
  },
  choiceButtonActive: {
    backgroundColor: "#1e3a5f",
    border: "1px solid #c9a84c",
    color: "#c9a84c",
  },
  errorText: {
    textAlign: "center",
    color: "#ff4444",
    fontSize: 14,
    marginTop: 12,
  },
  scoreContainer: {
    textAlign: "center",
    marginBottom: 20,
    padding: "16px",
    backgroundColor: "#0d1b2a",
    borderRadius: 12,
  },
  overallScoreLabel: {
    fontSize: 13,
    color: "#8899aa",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    margin: "0 0 4px 0",
  },
  overallScoreValue: {
    fontSize: 56,
    fontWeight: 700,
    color: "#c9a84c",
    margin: 0,
    lineHeight: 1,
  },
  overallScoreMax: {
    fontSize: 24,
    color: "#8899aa",
  },
  phaseScoreRow: {
    display: "flex",
    gap: 8,
    marginBottom: 20,
  },
  phaseScoreBox: {
    flex: 1,
    backgroundColor: "#0d1b2a",
    borderRadius: 10,
    padding: "12px 8px",
    textAlign: "center",
  },
  phaseScoreValue: {
    fontSize: 22,
    fontWeight: 700,
    color: "#c9a84c",
    margin: "0 0 4px 0",
  },
  phaseScoreLabel: {
    fontSize: 11,
    color: "#8899aa",
    margin: 0,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  sectionSubtitle: {
    fontSize: 13,
    color: "#8899aa",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 12,
    marginTop: 0,
  },
  angleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  },
  angleName: {
    fontSize: 12,
    color: "#ddddee",
    margin: 0,
    width: 140,
    flexShrink: 0,
  },
  angleBarBg: {
    flex: 1,
    height: 6,
    backgroundColor: "#1e3a5f",
    borderRadius: 99,
    overflow: "hidden",
  },
  angleBarFill: {
    height: "100%",
    borderRadius: 99,
    transition: "width 0.5s ease",
  },
  angleScore: {
    fontSize: 12,
    color: "#c9a84c",
    margin: 0,
    width: 28,
    textAlign: "right",
    flexShrink: 0,
  },feedbackTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 8,
    color: "#c9a84c",
  },
  turnsText: {
    fontSize: 14,
    color: "#8899aa",
    marginBottom: 16,
  },
  feedbackItem: {
    display: "flex",
    gap: 10,
    marginBottom: 14,
    alignItems: "flex-start",
  },
  feedbackBullet: {
    color: "#c9a84c",
    fontSize: 16,
    lineHeight: 1.6,
    flexShrink: 0,
  },
  feedbackText: {
    fontSize: 15,
    lineHeight: 1.6,
    color: "#ddddee",
    margin: 0,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "#c9a84c",
    marginBottom: 16,
    marginTop: 0,
  },
  emptyText: {
    color: "#8899aa",
    fontSize: 15,
    textAlign: "center",
    marginTop: 40,
  },
  sessionCard: {
    backgroundColor: "#112236",
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    border: "1px solid #1e3a5f",
    cursor: "pointer",
  },
  sessionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  sessionDate: {
    fontSize: 15,
    fontWeight: 700,
    color: "#ffffff",
    margin: 0,
  },
  sessionTime: {
    fontSize: 13,
    color: "#8899aa",
    margin: "2px 0 0 0",
  },
  sessionMeta: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  sessionBadge: {
    backgroundColor: "#1e3a5f",
    color: "#c9a84c",
    borderRadius: 99,
    padding: "3px 10px",
    fontSize: 12,
  },
  sessionExpanded: {
    marginTop: 12,
  },
  sessionDivider: {
    height: 1,
    backgroundColor: "#1e3a5f",
    marginBottom: 12,
  },
  sessionDetail: {
    fontSize: 14,
    color: "#ddddee",
    margin: "0 0 8px 0",
    lineHeight: 1.5,
  },
  sessionFeedbackTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#c9a84c",
    marginBottom: 10,
    marginTop: 12,
  },
  comingSoon: {
    textAlign: "center",
    paddingTop: 60,
  },
  comingSoonIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  comingSoonTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#c9a84c",
    marginBottom: 12,
  },
  comingSoonText: {
    fontSize: 15,
    color: "#8899aa",
    lineHeight: 1.6,
    maxWidth: 300,
    margin: "0 auto",
  },
  howStep: {
    fontSize: 16,
    fontWeight: 700,
    color: "#ffffff",
    margin: "0 0 8px 0",
  },
  howText: {
    fontSize: 14,
    color: "#aabbcc",
    lineHeight: 1.7,
    margin: 0,
  },
  footer: {
    textAlign: "center",
    color: "#3a5a7a",
    fontSize: 12,
    marginTop: 24,
    letterSpacing: "0.06em",
    lineHeight: 1.8,
  },
  footerLink: {
    color: "#8899aa",
    whiteSpace: "nowrap",
  },
};
function DrillsTab({ supabase, subscriptionStatus, onUpgrade }) {
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDrill, setActiveDrill] = useState(null);

  useEffect(() => {
    const loadDrills = async () => {
      const { data, error } = await supabase
        .from("drills")
        .select("*")
        .order("order_index");
      if (!error) setDrills(data);
      setLoading(false);
    };
    loadDrills();
  }, [supabase]);

  if (loading) return <p style={{ color: "#8899aa", textAlign: "center", marginTop: 40 }}>Loading drills...</p>;

  if (subscriptionStatus !== "premium") {
    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#c9a84c", marginBottom: 6, marginTop: 0 }}>Drills</h2>
        <div style={{ backgroundColor: "#112236", borderRadius: 16, padding: 20, border: "1px solid #1e3a5f", textAlign: "center" }}>
          <p style={{ fontSize: 22, margin: "0 0 8px 0" }}>🎬</p>
          <p style={{ color: "#ffffff", fontSize: 15, margin: "0 0 16px 0" }}>
            The full drill library is available on Premium plan.
          </p>
          <button style={styles.button} onClick={onUpgrade}>
            Upgrade to Premium
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#c9a84c", marginBottom: 6, marginTop: 0 }}>Drills</h2>
      <p style={{ fontSize: 13, color: "#8899aa", marginBottom: 20, marginTop: 0 }}>
        Tap a drill to watch the video
      </p>

      {activeDrill && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ backgroundColor: "#112236", borderRadius: 16, overflow: "hidden", border: "1px solid #c9a84c44" }}>
            <video
              key={activeDrill.video_url}
              controls
              autoPlay
              playsInline
              style={{ width: "100%", display: "block", maxHeight: 300, backgroundColor: "#000" }}
            >
              <source src={activeDrill.video_url} type="video/mp4" />
            </video>
            <div style={{ padding: 16 }}>
              <p style={{ fontSize: 16, fontWeight: 700, color: "#c9a84c", margin: "0 0 8px 0" }}>
                {activeDrill.title}
              </p>
              <p style={{ fontSize: 14, color: "#aabbcc", lineHeight: 1.6, margin: 0 }}>
                {activeDrill.description}
              </p>
              <button
                onClick={() => setActiveDrill(null)}
                style={{ marginTop: 12, backgroundColor: "transparent", border: "1px solid #1e3a5f", color: "#8899aa", borderRadius: 8, padding: "6px 14px", fontSize: 13, cursor: "pointer", fontFamily: "Georgia, serif" }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {drills.map((drill) => (
        <div
          key={drill.id}
          style={{
            backgroundColor: activeDrill?.id === drill.id ? "#1e3a5f" : "#112236",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 10,
            border: activeDrill?.id === drill.id ? "1px solid #c9a84c" : "1px solid #1e3a5f",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
          onClick={() => setActiveDrill(activeDrill?.id === drill.id ? null : drill)}
        >
          <span style={{ fontSize: 20 }}>🎬</span>
          <p style={{ fontSize: 15, color: activeDrill?.id === drill.id ? "#c9a84c" : "#ffffff", margin: 0, fontWeight: 600 }}>
            {drill.title}
          </p>
        </div>
      ))}
    </div>
  );
}